import {
  ComponentFactoryResolver,
  inject,
  Injectable,
  Injector,
  OnDestroy,
} from '@angular/core';
import {
  Feature,
  Point as geoJSONPoint,
  LineString,
  MultiLineString,
} from 'geojson';
import {
  CircleMarker,
  Content,
  Icon,
  Layer,
  LeafletMouseEvent,
  Marker,
  PathOptions,
  Polygon,
  Polyline,
} from 'leaflet';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeedbackAlertService } from '../../../shared/feedback-alert/service/feedback-alert.service';
import {
  RP_CABINET_CLICKED,
  RP_HC_CLICKED,
  RP_HP_CLICKED,
  RP_SPAN_CLICKED,
  RP_WELL_CLICKED,
  SPAN_ALREADY_REPORT_CLICKED,
} from '../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../shared/posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../../../shared/self-inspection-reports/service/entities';
import { RolloutProgressReportsService } from '../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { ExistingSpanPipe } from '../../../shared/util/existing-span.pipe';
import { PropertySpanPipe } from '../../../shared/util/property-span.pipe';
import { LandAgreementService } from '../../service/map/land-agreement/land-agreement.service';
import { PermitAreaService } from '../../service/map/permit/permit-area.service';
import { PointService } from '../../service/map/point/point.service';
import { SelectSpanModeService } from '../../service/map/span/select-span-mode/select-span-mode.service';
import { SpanService } from '../../service/map/span/span.service';
import {
  DistributionPointProperties,
  IconPaths,
  MultiHcPointProperties,
  MultiHpPointProperties,
  MultiWellPointProperties,
  PointLayerTypes,
  PointType,
  PopupTypes,
  SelectedMapItem,
  SpanProperties,
} from '../../span-progress-entities';
import {
  MultiPointTabs,
  ReportModalService,
} from '../../span-progress-report/report-modal.service';
import {
  DuctInteractionService,
  InteractionType,
} from '../duct-table/services/duct-interaction.service';
import { landAgreementStyles } from '../map-view-constants';
import { PopupContentComponent } from '../popup-content/popup-content.component';

export type LayerTypes = CircleMarker | Polyline | Polygon;

export const enum OptionTypes {
  Point = 'point',
  Span = 'span',
}
export interface HighlightedFeatureInfo {
  layer: LayerTypes | Marker;
  options: ExtendedPathOptions | OptionTypes;
}

interface ExtendedPathOptions extends PathOptions {
  radius?: number;
}

const popupMaxWidth = () => {
  const maxWidth = 320;
  const marginBuffer = 35;
  return screen.width < maxWidth + marginBuffer
    ? screen.width - marginBuffer
    : maxWidth;
};

@Injectable({ providedIn: 'root' })
export class MapInteractionService implements OnDestroy {
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  private readonly reportV3Service = inject(RolloutProgressReportsService);
  private readonly feedbackAlertService = inject(FeedbackAlertService);
  private readonly propertySpanPipe = inject(PropertySpanPipe);
  private readonly existingSpanPipe = inject(ExistingSpanPipe);

  private highlightedFeatures: HighlightedFeatureInfo[] = [];
  private cancelSub$ = new Subject<void>();
  private currentPointRadius: number | undefined;

  private readonly isSelectSpanMode =
    this.selectSpanModeService.isSelectSpanMode;

  private selectedResultControl$ = new BehaviorSubject<SelectedMapItem>(
    {} as SelectedMapItem,
  );
  private selectedSpanControl$ = new BehaviorSubject<Polyline | null>(null);

  selectedLayer: LayerTypes | null;
  v3Reports = this.reportV3Service.storedV3Reports;
  originalReportSpanIds = this.selectSpanModeService.originalReportSpanIds;

  constructor(
    private spanService: SpanService,
    private pointService: PointService,
    private landAgreementService: LandAgreementService,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private ductInteractionService: DuctInteractionService,
    private permitAreaService: PermitAreaService,
    private modalService: ReportModalService,
    private analyticsService: AnalyticsService,
  ) {
    this.initLayerClickSubscriptions();
  }

  ngOnDestroy() {
    this.cancelSub$.next();
  }

  private initLayerClickSubscriptions() {
    this.spanService
      .getClicked$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe(({ e }) => {
        this.onClickedSpan(e);
      });
    this.landAgreementService
      .getClicked$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe(({ e }) => {
        this.onClickedLandAgreement(e);
      });
    this.pointService
      .getClicked$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe(({ e, args }) => {
        if (args) {
          this.onClickedPoint(e, args['popupType'], args['cluster']);
        }
      });
    this.ductInteractionService
      .getSelectedDuct$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((selectedDuct) => {
        let spanArray: Layer[] = [];
        if (selectedDuct.interaction === 'ductListInteraction') {
          this.blurFeatureSelection('ductListInteraction');

          if (selectedDuct.duct) {
            spanArray =
              this.spanService.getSpanLayersBySpanIds(
                selectedDuct.duct.spanIds,
              ) ?? [];
            spanArray.forEach((span) => {
              this.highlightSingleSpan(span as Polyline);
            });
          }
        }
      });

    this.permitAreaService
      .getClicked$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe(({ e }) => {
        this.onClickedPermitArea(e);
      });
  }

  selectSpan(e: LeafletMouseEvent) {
    const spanLayer: Polyline = e.sourceTarget;
    const currentSelection = this.selectedLayer;
    this.blurFeatureSelection('mapInteraction');
    if (spanLayer === currentSelection) {
      this.ductInteractionService.setSearchValue('');
      const feature = this.getGeoJsonFeature<SpanProperties>(spanLayer.feature);
      spanLayer.setStyle(
        this.spanService.getSpanColorByProgress(feature.properties),
      );
      return;
    }

    this.selectedLayer = spanLayer;
    this.handleHighlightSpans(spanLayer);

    spanLayer
      .bindPopup(this.onOpenPopup(spanLayer, PopupTypes.Span), {
        maxWidth: popupMaxWidth(),
        closeButton: false,
      })
      .openPopup(e.latlng);

    this.ductInteractionService.setSearchValue(
      (this.selectedLayer.feature?.properties as SpanProperties).serialNumber,
    );
    this.selectedResultControl$.next({} as SelectedMapItem);
  }

  initMultiSpan() {
    this.blurFeatureSelectionForMultiSpan();
  }

  selectSpansForMultiSpan(e: LeafletMouseEvent) {
    const spanLayer: Polyline = e.sourceTarget;
    this.selectedLayer?.closePopup();
    this.selectedLayer = null;

    const spanNotPartOfOtherReport =
      this.checkSpanExistsInOtherMultiSpanReport(spanLayer);

    const isEligableSpan = !this.checkIsEligableToSelect(spanLayer);

    if (spanNotPartOfOtherReport && isEligableSpan) {
      const spanLayers = this.selectSpanModeService.selectedSpanLayers();

      if (spanLayers.includes(spanLayer)) {
        this.removeSpanFromMultiSpanSelectMode(spanLayer);
      } else {
        this.addSpanToMultiSpanSelectMode(spanLayer);
      }
    }
  }

  checkSpanExistsInOtherMultiSpanReport = (spanLayer: Polyline): boolean => {
    if (!this.v3Reports().SPAN) {
      return true;
    }

    const existingV3Report: RolloutProgressReportEntities.v3.ReportResponse =
      this.v3Reports().SPAN[
        (spanLayer.feature?.properties as SpanProperties).spanId
      ];
    const currentReport = this.originalReportSpanIds().includes(
      (spanLayer.feature?.properties as SpanProperties).spanId,
    );

    if (existingV3Report && !currentReport) {
      this.feedbackAlertService.showAlert(
        'critical',
        'selectSpanMode.spanExistsInOtherReport',
        3000,
      );
      this.analyticsService.logEvent(SPAN_ALREADY_REPORT_CLICKED);
      return false;
    }

    return true;
  };

  checkIsEligableToSelect = (spanLayer: Polyline): boolean => {
    const isPropertySpan = this.propertySpanPipe.transform(
      (spanLayer.feature?.properties as SpanProperties).permissionTypeId,
    );

    if (isPropertySpan) {
      this.feedbackAlertService.showAlert(
        'critical',
        'selectSpanMode.propertySpanCannotBeReported',
        3000,
      );
    }

    const isExistingSpan = this.existingSpanPipe.transform(
      (spanLayer.feature?.properties as SpanProperties).accommodationMethodId,
    );

    if (isExistingSpan) {
      this.feedbackAlertService.showAlert(
        'critical',
        'selectSpanMode.existingSpanCannotBeReported',
        3000,
      );
    }

    return isPropertySpan || isExistingSpan;
  };

  removeSpanFromMultiSpanSelectMode = (spanLayer: Polyline): void => {
    this.selectSpanModeService.removeSelectedSpanLayer(spanLayer);
    this.highlightedFeatures.forEach((highlightedInfo) => {
      if (highlightedInfo.layer === spanLayer) {
        this.resetStyle(highlightedInfo);
      }
    });
  };

  addSpanToMultiSpanSelectMode = (spanLayer: Polyline): void => {
    this.selectSpanModeService.addSelectedSpanLayer(spanLayer);
    this.highlightSingleSpan(spanLayer);
  };

  getSelectedAndOriginalSpanLayers = (): {
    spanLayers: Polyline[];
    selectedAndOriginalSpanLayers: HighlightedFeatureInfo[];
  } => {
    const spanLayers: Polyline[] =
      this.selectSpanModeService.selectedSpanLayers();
    const selectedAndOriginalSpanLayers: HighlightedFeatureInfo[] = Array.from(
      ...[
        new Set([
          ...this.highlightedFeatures,
          ...this.selectSpanModeService.originalHighlightedLayers(),
        ]),
      ],
    );

    return { spanLayers, selectedAndOriginalSpanLayers };
  };

  resetMultiSpan = (): void => {
    const { spanLayers, selectedAndOriginalSpanLayers } =
      this.getSelectedAndOriginalSpanLayers();

    if (spanLayers.length) {
      this.resetStyleForSelectedAndDeselectedMultiSpans(
        spanLayers,
        selectedAndOriginalSpanLayers,
      );
      return;
    }

    this.resetStyleWhenNoSelection();
  };

  resetStyleForSelectedAndDeselectedMultiSpans = (
    spanLayers: Polyline[],
    selectedAndOriginalSpanLayers: HighlightedFeatureInfo[],
  ) => {
    selectedAndOriginalSpanLayers.forEach(
      (highlightInfo: HighlightedFeatureInfo) => {
        const layer = highlightInfo.layer;

        if (
          layer instanceof Polyline &&
          (spanLayers.includes(layer as Polyline) ||
            this.selectSpanModeService
              .originalHighlightedLayers()
              .includes(highlightInfo))
        ) {
          this.resetStyle(highlightInfo);
        }
      },
    );
    this.selectSpanModeService.clearSelectedSpanLayers();
  };

  resetStyleWhenNoSelection = (): void => {
    this.selectSpanModeService
      .originalHighlightedLayers()
      .forEach((highlightInfo) => {
        this.resetStyle(highlightInfo);
      });
  };

  onClickedSpan(e: LeafletMouseEvent) {
    if (this.isSelectSpanMode()) {
      this.selectSpansForMultiSpan(e);
    } else {
      this.selectSpan(e);
    }

    this.analyticsService.logEvent(RP_SPAN_CLICKED);
  }

  onClickedPermitArea(e: LeafletMouseEvent) {
    if (this.isSelectSpanMode()) {
      return;
    }
    const permitAreaLayer: Polygon = e.sourceTarget;
    const currentSelection = this.selectedLayer;
    this.blurFeatureSelection('mapInteraction');
    if (permitAreaLayer === currentSelection) {
      return;
    }
    this.selectedLayer = permitAreaLayer;
    this.highlightFeature(permitAreaLayer, landAgreementStyles.selected);
    permitAreaLayer
      .bindPopup(this.onOpenPopup(permitAreaLayer, PopupTypes.PermitArea), {
        maxWidth: popupMaxWidth(),
        closeButton: false,
      })
      .openPopup(e.latlng);
  }

  onClickedLandAgreement(e: LeafletMouseEvent) {
    if (this.isSelectSpanMode()) {
      return;
    }
    const landAgreementLayer: Polygon = e.sourceTarget;
    const currentSelection = this.selectedLayer;
    this.blurFeatureSelection('mapInteraction');
    if (landAgreementLayer === currentSelection) {
      return;
    }

    this.selectedLayer = landAgreementLayer;
    this.highlightFeature(landAgreementLayer, landAgreementStyles.selected);

    landAgreementLayer
      .bindPopup(
        this.onOpenPopup(landAgreementLayer, PopupTypes.LandAgreement),
        { maxWidth: popupMaxWidth(), closeButton: false },
      )
      .openPopup(e.latlng);
  }

  onClickedPoint(
    e: LeafletMouseEvent,
    typeId: PopupTypes,
    data?: Feature<geoJSONPoint, DistributionPointProperties>[],
    expandedPointId?: string,
  ) {
    if (typeId === PopupTypes.MultiHP) {
      this.modalService.setModalState({
        multiPointModal: {
          open: true,
          multiPoint: {
            data: {
              hp: data as unknown as MultiHpPointProperties[],
              hc: [],
              well: [],
            },
            tabs: [MultiPointTabs.HP],
          },
        },
      });
      return;
    }
    if (typeId === PopupTypes.MultiHC) {
      this.modalService.setModalState({
        multiPointModal: {
          open: true,
          multiPoint: {
            data: {
              hc: data as unknown as MultiHcPointProperties[],
              hp: [],
              well: [],
            },
            tabs: [MultiPointTabs.HC],
          },
        },
      });
      return;
    }

    if (typeId === PopupTypes.MultiHouseHold) {
      this.modalService.setModalState({
        multiPointModal: {
          open: true,
          multiPoint: {
            data: {
              well: [],
              hc: data?.filter(
                (point) => point.properties.markerTypeId === PointType.Hc,
              ) as unknown as MultiHcPointProperties[],
              hp: data
                ?.filter(
                  (point) => point.properties.markerTypeId === PointType.Hp,
                )
                .map((hpPoint) => ({
                  ...hpPoint,
                  status: this.pointService.mapToCorrectReportStatus(
                    hpPoint.properties,
                    '',
                  ),
                })) as unknown as MultiHpPointProperties[],
            },
            tabs: [MultiPointTabs.HC, MultiPointTabs.HP],
          },
        },
      });
      return;
    }

    if (typeId === PopupTypes.MultiWell) {
      this.modalService.setModalState({
        multiPointModal: {
          open: true,
          multiPoint: {
            data: {
              well: data as unknown as MultiWellPointProperties[],
              hc: [],
              hp: [],
            },
            tabs: [MultiPointTabs.WELL],
          },
        },
      });
      return;
    }

    const pointLayer: CircleMarker = e.sourceTarget;
    const currentSelection = this.selectedLayer;
    this.blurFeatureSelection('mapInteraction');

    if (pointLayer === currentSelection) {
      this.resetHighlightBasedOnPointType(e, pointLayer);
      return;
    }

    this.selectedLayer = pointLayer;
    const properties = this.getGeoJsonFeature<DistributionPointProperties>(
      pointLayer.feature,
    ).properties;

    this.highlightBasedOnPointType(e, pointLayer, properties);
    this.bindToolTip(pointLayer);
    this.selectedResultControl$.next({
      ...properties,
      typeId,
      clickedObject: pointLayer,
      expandedPointId,
    });
    this.analyticsPointClicked(properties.markerTypeId);
  }

  getSelectedResultControl$() {
    return this.selectedResultControl$.asObservable();
  }

  setSelectedResultControl$(selectedMapItem: SelectedMapItem) {
    this.selectedResultControl$.next(selectedMapItem);
  }

  onSelectSearchedItem(pointLayer: CircleMarker) {
    this.blurFeatureSelection('mapInteraction');

    if (!pointLayer.feature) {
      return;
    }

    const properties = this.getGeoJsonFeature<DistributionPointProperties>(
      pointLayer.feature,
    ).properties;

    this.highlightFeature(
      pointLayer,
      { color: 'cyan', weight: 4 },
      OptionTypes.Point,
    );
    if (
      properties.markerTypeId === PointType.Cabinet ||
      properties.markerTypeId === PointType.CableWell ||
      properties.markerTypeId === PointType.SlingWell
    ) {
      this.setSelectedResultControl$({
        ...properties,
        typeId: PopupTypes.DistPoint,
        clickedObject: pointLayer,
      });
    }
    if (
      properties.markerTypeId === PointType.Hc ||
      properties.markerTypeId === PointType.Hp
    ) {
      this.setSelectedResultControl$({
        ...properties,
        typeId: PopupTypes.Households,
        clickedObject: pointLayer,
      });
    }
    if (properties.markerTypeId === PointType.Site) {
      this.setSelectedResultControl$({
        ...properties,
        typeId: PopupTypes.Site,
        clickedObject: pointLayer,
      });
    }
    if (properties.markerTypeId === PointType.Hub) {
      this.setSelectedResultControl$({
        ...properties,
        typeId: PopupTypes.Hub,
        clickedObject: pointLayer,
      });
    }

    this.selectedLayer = pointLayer;
    this.bindToolTip(pointLayer);
    this.selectDuctsToHighlight(pointLayer);
  }

  selectSpanFromReportPanel(spanLayer: Polyline) {
    if (this.isSelectSpanMode()) {
      return;
    }

    this.selectSpan({
      sourceTarget: spanLayer,
      latlng: spanLayer.getCenter(),
    } as LeafletMouseEvent);
    this.selectedSpanControl$.next(spanLayer);
  }

  selectPointFromReportPanel(
    pointLayer: CircleMarker | Marker,
    popupType: PopupTypes,
    expandedPointId?: string,
  ) {
    this.onClickedPoint(
      { sourceTarget: pointLayer } as LeafletMouseEvent,
      popupType,
      undefined,
      expandedPointId,
    );
  }

  getSelectedSpanControl$() {
    return this.selectedSpanControl$.asObservable();
  }

  bindToolTip(pointLayer: CircleMarker) {
    const properties = this.getGeoJsonFeature<DistributionPointProperties>(
      pointLayer.feature,
    ).properties;
    pointLayer.bindTooltip(
      (properties.markerTypeId === PointType.Cabinet ||
        properties.markerTypeId === PointType.CableWell ||
        properties.markerTypeId === PointType.Site ||
        properties.markerTypeId === PointType.Hub) &&
        properties.telematorId
        ? `${properties.telematorId} (${properties.name})`
        : properties.name,
      { permanent: true },
    );
  }

  blurFeatureSelection(interactionType: InteractionType) {
    this.selectedLayer?.closeTooltip();
    this.selectedLayer?.closePopup();
    this.selectedLayer = null;

    this.highlightedFeatures.forEach((highlightedInfo) => {
      this.resetStyle(highlightedInfo);
    });

    this.highlightedFeatures = [];
    if (interactionType === 'mapInteraction') {
      this.ductInteractionService.setSelectedDuct({
        duct: null,
        interaction: interactionType,
      });
    }
  }

  blurFeatureSelectionForMultiSpan() {
    this.selectedLayer?.closeTooltip();
    this.selectedLayer?.closePopup();
    this.selectedLayer = null;
  }

  onOpenPopup(
    clickedObject: CircleMarker | Polyline | Polygon,
    typeId: PopupTypes,
  ): Content {
    const feature = this.getGeoJsonFeature(clickedObject.feature);

    if (!feature.properties) {
      return '';
    }
    const container = this.resolver
      .resolveComponentFactory(PopupContentComponent)
      .create(this.injector);
    container.instance.properties = feature.properties;
    container.instance.typeId = typeId;
    container.instance.clickedObject = clickedObject;
    container.changeDetectorRef.detectChanges();
    clickedObject.on('popupclose', () => {
      clickedObject.unbindPopup();
      container.destroy();
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return container.location.nativeElement;
  }

  private getGeoJsonFeature<T = any>(
    feature?: GeoJSON.Feature<GeoJSON.Geometry, T>,
  ) {
    return feature as GeoJSON.Feature<GeoJSON.Geometry, T>;
  }

  highlightBasedOnPointType(
    e: LeafletMouseEvent,
    pointLayer: CircleMarker<any>,
    properties: DistributionPointProperties,
  ) {
    switch (properties.markerTypeId) {
      case PointType.Cabinet:
        this.highlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(properties, 'CABINET'),
        );
        break;
      case PointType.Site:
        this.highlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(properties, 'SITE'),
        );
        break;
      case PointType.Hub:
        this.highlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(properties, 'HUB'),
        );
        break;
      case PointType.Hp:
        this.highlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(properties, 'HP'),
        );
        break;
      case PointType.CableWell:
      case PointType.SlingWell:
        this.highlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(properties, 'MANHOLE'),
        );
        break;
      case PointType.DuctEndpoint:
        this.highlightIconFeature(
          e.sourceTarget as Marker,
          IconPaths.SDP_UNKNOWN,
        );
        break;
      default:
        this.highlightFeature(
          pointLayer,
          { color: 'cyan', weight: 4 },
          OptionTypes.Point,
        );
        break;
    }
    this.selectDuctsToHighlight(pointLayer);
  }

  resetHighlightBasedOnPointType(
    e: LeafletMouseEvent,
    pointLayer: CircleMarker<any>,
  ) {
    const feature = this.getGeoJsonFeature<DistributionPointProperties>(
      pointLayer.feature,
    );
    switch (feature.properties.markerTypeId) {
      case PointType.Cabinet:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(feature.properties, 'CABINET'),
        );
        break;
      case PointType.Site:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(feature.properties, 'SITE'),
        );
        break;
      case PointType.Hub:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(feature.properties, 'HUB'),
        );
        break;
      case PointType.Hp:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(feature.properties, 'HP'),
        );
        break;
      case PointType.SlingWell:
      case PointType.CableWell:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          this.getImagePathForSinglePoint(feature.properties, 'MANHOLE'),
        );
        break;
      case PointType.DuctEndpoint:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          IconPaths.SDP_UNKNOWN,
        );
        break;
      case PointType.DuctPoint:
        this.resetHighlightIconFeature(
          e.sourceTarget as Marker,
          this.pointService.getImagePathForAggregatedPoint(feature.properties),
        );
        break;
      default:
        pointLayer.setStyle(
          this.pointService.getPointColorByProgress(feature.properties),
        );
        break;
    }
  }

  selectDuctsToHighlight(point: CircleMarker<DistributionPointProperties>) {
    const properties = this.getGeoJsonFeature<DistributionPointProperties>(
      point.feature,
    ).properties;

    if (properties.markerTypeId === PointType.Cabinet) {
      this.highlightConnections(properties.ductIds, ['HC', 'SDP', 'KP', 'HP']);
    }
    if (
      properties.markerTypeId === PointType.Hc ||
      properties.markerTypeId === PointType.Hp
    ) {
      this.highlightConnections([properties.ductId], ['SDP']);
      this.highlightFos(properties.ductId);
    }
    if (properties.markerTypeId === PointType.DuctEndpoint) {
      this.highlightFos(properties.ductId);
      this.highlightConnections([properties.ductId], ['HC', 'HP']);
    }
  }

  private highlightConnections(ductIds: string[], pointLayerNames: string[]) {
    pointLayerNames.forEach((pointLayerName) => {
      const pointLayer = this.pointService.getLayerData()[pointLayerName];
      if (!pointLayer) {
        return;
      }

      pointLayer.getLayers().forEach((point: CircleMarker) => {
        if (
          ductIds?.includes(
            this.getGeoJsonFeature<DistributionPointProperties>(point.feature)
              .properties.ductId,
          )
        ) {
          this.highlightFeature(point, {
            fillColor: 'cyan',
            color: 'cyan',
          });
        }
      });
    });
  }

  private findFosPoint(ductId: string) {
    const fosLayerData = this.pointService.getLayerData()['CABINET'];
    if (!fosLayerData) {
      return null;
    }

    const fosPoints = fosLayerData.getLayers() as CircleMarker[];
    const fosLayer = fosPoints.find((fosPoint: CircleMarker) =>
      this.getGeoJsonFeature<DistributionPointProperties>(
        fosPoint.feature,
      ).properties.ductIds.includes(ductId),
    );
    return fosLayer;
  }

  private highlightFos(ductId: string) {
    const fosLayer = this.findFosPoint(ductId);
    if (fosLayer) {
      this.highlightFeature(fosLayer, {
        fillColor: 'cyan',
        color: 'cyan',
      });
    }
  }

  private highlightFeature(
    layer: LayerTypes | Marker,
    options: ExtendedPathOptions,
    optionTypes?: OptionTypes,
  ) {
    this.highlightedFeatures = [
      ...this.highlightedFeatures,
      { layer, options: optionTypes ?? { ...layer.options } },
    ];
    switch (
      (layer.feature?.properties as DistributionPointProperties).markerTypeId
    ) {
      case PointType.Cabinet:
        this.highlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(layer.feature?.properties, 'CABINET'),
        );
        break;
      case PointType.Site:
        this.highlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(layer.feature?.properties, 'SITE'),
        );
        break;
      case PointType.Hub:
        this.highlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(layer.feature?.properties, 'HUB'),
        );
        break;
      case PointType.Hp:
        this.highlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(layer.feature?.properties, 'HP'),
        );
        break;
      case PointType.CableWell:
      case PointType.SlingWell:
        this.highlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(layer.feature?.properties, 'MANHOLE'),
        );
        break;
      case PointType.DuctEndpoint:
        this.highlightIconFeature(layer as Marker, IconPaths.SDP_UNKNOWN);
        break;
      case PointType.DuctPoint:
        this.highlightIconFeature(
          layer as Marker,
          this.pointService.getImagePathForAggregatedPoint(
            layer.feature?.properties,
          ),
        );
        break;
      case PointType.MultiHP:
        break;
      case PointType.MultiHC:
        break;
      case PointType.MultiHouseHold:
        break;
      default:
        (layer as LayerTypes).setStyle(options);
        break;
    }
  }

  private highlightIconFeature(layer: Marker, path: IconPaths) {
    const currentIcon = layer.getIcon();
    const icon = new Icon({
      iconUrl: `assets/leaflet/images/point-icons/${path}-highlight.svg`,
      iconAnchor: currentIcon.options.iconAnchor,
      iconSize: currentIcon.options.iconSize,
    });
    layer.setIcon(icon);
    this.highlightedFeatures = [
      ...this.highlightedFeatures,
      { layer, options: { ...layer.options } },
    ];
  }

  private handleHighlightSpans(
    spanLayer: Polyline<LineString | MultiLineString, any>,
  ) {
    const spanReport = this.getReportForSpan(spanLayer);
    if (spanReport) {
      const spanId = this.getGeoJsonFeature<SpanProperties>(spanLayer.feature)
        .properties.spanId;
      const linkedSpanLayers = this.spanService.getLinkedSpanLayersFromReport(
        spanReport,
        spanId,
      );
      if (linkedSpanLayers.length) {
        this.highlightLinkedSpans(spanLayer, linkedSpanLayers);
        return;
      }
    }
    this.highlightSingleSpan(spanLayer);
  }

  private highlightSingleSpan(
    spanLayer: Polyline<LineString | MultiLineString, any>,
  ) {
    this.highlightFeature(
      spanLayer,
      { color: 'cyan', weight: 6 },
      OptionTypes.Span,
    );
  }

  private highlightLinkedSpans(
    spanLayer: Polyline<LineString | MultiLineString, any>,
    linkedSpanLayers: Polyline<LineString | MultiLineString, any>[],
  ) {
    this.highlightFeature(
      spanLayer,
      { color: 'cyan', weight: 12, lineCap: 'butt' },
      OptionTypes.Span,
    );
    linkedSpanLayers.forEach((linkedLayer) => {
      this.highlightFeature(
        linkedLayer,
        { color: 'cyan', weight: 5 },
        OptionTypes.Span,
      );
    });
  }

  private resetHighlightIconFeature(layer: Marker, path: IconPaths) {
    const currentIcon = layer.getIcon();
    const icon = new Icon({
      iconUrl: `assets/leaflet/images/point-icons/${path}.svg`,
      iconAnchor: currentIcon.options.iconAnchor,
      iconSize: currentIcon.options.iconSize,
    });
    layer.setIcon(icon);
  }

  resetStyle({ layer, options }: HighlightedFeatureInfo) {
    let styleOptions: ExtendedPathOptions;
    switch (options) {
      case OptionTypes.Point:
        styleOptions = this.pointService.getPointColorByProgress(
          this.getGeoJsonFeature<DistributionPointProperties>(layer.feature)
            .properties,
        );
        this.currentPointRadius = (
          layer.options as ExtendedPathOptions
        )?.radius;
        break;
      case OptionTypes.Span:
        styleOptions = this.spanService.getSpanColorByProgress(
          this.getGeoJsonFeature<SpanProperties>(layer.feature).properties,
        );
        break;
      default:
        styleOptions = { ...options, radius: this.currentPointRadius };
        break;
    }

    const markerTypeProperties = layer.feature
      ?.properties as DistributionPointProperties;

    this.resetStyleIcons(layer, markerTypeProperties, styleOptions);
  }

  resetStyleIcons(
    layer: Marker<any> | LayerTypes,
    markerTypeProperties: DistributionPointProperties,
    styleOptions: ExtendedPathOptions,
  ) {
    switch (markerTypeProperties.markerTypeId) {
      case PointType.Cabinet:
        this.resetHighlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(markerTypeProperties, 'CABINET'),
        );
        break;
      case PointType.Site:
        this.resetHighlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(markerTypeProperties, 'SITE'),
        );
        break;
      case PointType.Hub:
        this.resetHighlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(markerTypeProperties, 'HUB'),
        );
        break;
      case PointType.Hp:
        this.resetHighlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(markerTypeProperties, 'HP'),
        );
        break;
      case PointType.CableWell:
      case PointType.SlingWell:
        this.resetHighlightIconFeature(
          layer as Marker,
          this.getImagePathForSinglePoint(markerTypeProperties, 'MANHOLE'),
        );
        break;
      case PointType.DuctEndpoint:
        this.resetHighlightIconFeature(layer as Marker, IconPaths.SDP_UNKNOWN);
        break;
      case PointType.DuctPoint:
        this.resetHighlightIconFeature(
          layer as Marker,
          this.pointService.getImagePathForAggregatedPoint(
            markerTypeProperties,
          ),
        );
        break;
      default:
        (layer as LayerTypes).setStyle(styleOptions);
        break;
    }
  }

  private getReportForSpan(
    spanLayer: Polyline<LineString | MultiLineString, any>,
  ): RolloutProgressReportEntities.v3.ReportResponse | undefined {
    return this.reportV3Service.getStoredV3ReportsByMarkerType(
      RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
    )?.[
      this.getGeoJsonFeature<SpanProperties>(spanLayer.feature).properties
        .spanId
    ];
  }

  getImagePathForSinglePoint(
    properties: DistributionPointProperties,
    pointType: PointLayerTypes,
  ) {
    const status = this.pointService.mapToCorrectReportStatus(
      properties,
      pointType,
    );

    return `${pointType}/${status}` as IconPaths;
  }

  analyticsPointClicked(pointType: PointType) {
    switch (pointType) {
      case PointType.Hc:
        this.analyticsService.logEvent(RP_HC_CLICKED, {});
        break;
      case PointType.Cabinet:
        this.analyticsService.logEvent(RP_CABINET_CLICKED, {});
        break;
      case PointType.CableWell:
        this.analyticsService.logEvent(RP_WELL_CLICKED, {});
        break;
      case PointType.SlingWell:
        this.analyticsService.logEvent(RP_WELL_CLICKED, {});
        break;
      case PointType.Hp:
        this.analyticsService.logEvent(RP_HP_CLICKED, {});
        break;
    }
  }
}
